/**
 * AI 비용/사용량 관리 서비스
 * ilouli-ai API와 연동하여 AI 사용량 통계 조회
 */

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.ilouli.com';

const TOKEN_KEY = 'ilouli_token';

// 쿠키에서 토큰 가져오기
const getToken = () => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === TOKEN_KEY) {
      return decodeURIComponent(value);
    }
  }
  try {
    const backupToken = localStorage.getItem(`${TOKEN_KEY}_backup`);
    if (backupToken) return backupToken;
  } catch (e) {
    console.warn('Failed to get backup token:', e);
  }
  return null;
};

// 공통 헤더
const getHeaders = () => {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// AI 제공업체별 단가 ($/1M tokens, 2026-01 기준)
export const AI_PRICING = {
  groq: {
    name: 'Groq',
    models: {
      'llama-3.1-8b': { input: 0.05, output: 0.08 },
      'llama-3.1-70b': { input: 0.35, output: 0.40 },
      'mixtral-8x7b': { input: 0.24, output: 0.24 }
    },
    tier: 'free',
    status: 'active'
  },
  gemini: {
    name: 'Google Gemini',
    models: {
      'gemini-2.5-flash': { input: 0, output: 0 },
      'gemini-2.5-pro': { input: 1.25, output: 5.00 }
    },
    tier: 'free',
    status: 'active'
  },
  anthropic: {
    name: 'Claude (Anthropic)',
    models: {
      'claude-3-5-sonnet': { input: 3.00, output: 15.00 },
      'claude-3-5-haiku': { input: 0.25, output: 1.25 },
      'claude-3-opus': { input: 15.00, output: 75.00 }
    },
    tier: 'paid',
    status: 'active'
  },
  openai: {
    name: 'OpenAI',
    models: {
      'gpt-4o': { input: 2.50, output: 10.00 },
      'gpt-4o-mini': { input: 0.15, output: 0.60 },
      'gpt-4-turbo': { input: 10.00, output: 30.00 }
    },
    tier: 'paid',
    status: 'inactive'
  }
};

/**
 * 토큰 수로 비용 계산
 */
export const calculateCost = (provider, model, inputTokens, outputTokens) => {
  const providerPricing = AI_PRICING[provider?.toLowerCase()];
  if (!providerPricing) return 0;

  const modelPricing = providerPricing.models[model];
  if (!modelPricing) return 0;

  const inputCost = (inputTokens / 1_000_000) * modelPricing.input;
  const outputCost = (outputTokens / 1_000_000) * modelPricing.output;

  return inputCost + outputCost;
};

/**
 * AI 사용량 통계 조회
 */
export const getAiUsageStats = async (days = 30) => {
  const response = await fetch(`${API_BASE}/api/ai/ai-usage/stats?days=${days}`, {
    headers: getHeaders(),
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

/**
 * AI 사용량 로그 조회
 */
export const getAiUsageLogs = async (options = {}) => {
  const { contentType, feature, provider, limit = 50, offset = 0 } = options;
  let url = `${API_BASE}/api/ai/ai-usage/logs?limit=${limit}&offset=${offset}`;

  if (contentType) url += `&contentType=${encodeURIComponent(contentType)}`;
  if (feature) url += `&feature=${encodeURIComponent(feature)}`;
  if (provider) url += `&provider=${encodeURIComponent(provider)}`;

  const response = await fetch(url, {
    headers: getHeaders(),
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

/**
 * 콘텐츠 타입 목록 조회
 */
export const getContentTypes = async () => {
  const response = await fetch(`${API_BASE}/api/ai/ai-usage/content-types`, {
    headers: getHeaders(),
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

/**
 * 기능 목록 조회
 */
export const getFeatures = async () => {
  const response = await fetch(`${API_BASE}/api/ai/ai-usage/features`, {
    headers: getHeaders(),
    credentials: 'include'
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

/**
 * 예산 설정 조회/저장 (localStorage 기반, 추후 DB로 이전)
 */
const BUDGET_KEY = 'ai_budget_settings';

export const getBudgetSettings = () => {
  try {
    const saved = localStorage.getItem(BUDGET_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.warn('Failed to get budget settings:', e);
  }
  // 기본값
  return {
    monthlyBudget: 50,
    dailyLimit: 5,
    alertThreshold: 80, // 예산의 80% 도달 시 알림
    notifications: true
  };
};

export const saveBudgetSettings = (settings) => {
  try {
    localStorage.setItem(BUDGET_KEY, JSON.stringify(settings));
    return true;
  } catch (e) {
    console.error('Failed to save budget settings:', e);
    return false;
  }
};

export default {
  AI_PRICING,
  calculateCost,
  getAiUsageStats,
  getAiUsageLogs,
  getContentTypes,
  getFeatures,
  getBudgetSettings,
  saveBudgetSettings
};
